/**
 * Sprint Service
 * AIDLC Administrative Backend
 *
 * API calls for sprint CRUD, board data, and burndown operations.
 */

import apiClient from './api';
import type { Sprint, SprintBoardData, BurndownData } from '../types/projects';

const SPRINTS_PATH = '/sprints';

/**
 * Fetch all sprints for a project
 */
export async function fetchSprints(projectId: string): Promise<Sprint[]> {
  const response = await apiClient.get<Sprint[]>(`${SPRINTS_PATH}?projectId=${encodeURIComponent(projectId)}`);
  return response.data;
}

/**
 * Create a new sprint
 */
export async function createSprint(data: {
  projectId: string;
  name: string;
  goal: string;
  startDate: string;
  endDate: string;
  backlogItemIds?: string[];
}): Promise<Sprint> {
  const response = await apiClient.post<Sprint>(SPRINTS_PATH, data);
  return response.data;
}

/**
 * Get a single sprint by ID
 */
export async function getSprint(id: string): Promise<Sprint> {
  const response = await apiClient.get<Sprint>(`${SPRINTS_PATH}/${id}`);
  return response.data;
}

/**
 * Update sprint details (name, goal, dates)
 */
export async function updateSprint(id: string, data: Partial<Pick<Sprint, 'name' | 'goal' | 'startDate' | 'endDate'>>): Promise<Sprint> {
  const response = await apiClient.put<Sprint>(`${SPRINTS_PATH}/${id}`, data);
  return response.data;
}

/**
 * Activate a sprint (moves from planning -> active)
 */
export async function activateSprint(id: string): Promise<Sprint> {
  const response = await apiClient.put<Sprint>(`${SPRINTS_PATH}/${id}/activate`, {});
  return response.data;
}

/**
 * Complete a sprint (moves from active -> completed, calculates velocity)
 */
export async function completeSprint(id: string, retrospective?: {
  whatWentWell: string[];
  whatToImprove: string[];
  actionItems: string[];
}): Promise<Sprint> {
  const response = await apiClient.put<Sprint>(`${SPRINTS_PATH}/${id}/complete`, { retrospective });
  return response.data;
}

/**
 * Add backlog items to a sprint
 */
export async function addItemsToSprint(sprintId: string, itemIds: string[]): Promise<Sprint> {
  const response = await apiClient.post<Sprint>(`${SPRINTS_PATH}/${sprintId}/items`, { itemIds });
  return response.data;
}

/**
 * Remove a single item from a sprint
 */
export async function removeItemFromSprint(sprintId: string, itemId: string): Promise<void> {
  await apiClient.delete(`${SPRINTS_PATH}/${sprintId}/items/${itemId}`);
}

/**
 * Get Kanban board data for a sprint
 */
export async function getSprintBoard(sprintId: string): Promise<SprintBoardData> {
  const response = await apiClient.get<SprintBoardData>(`${SPRINTS_PATH}/${sprintId}/board`);
  return response.data;
}

/**
 * Get burndown chart data for a sprint
 */
export async function getSprintBurndown(sprintId: string): Promise<BurndownData> {
  const response = await apiClient.get<BurndownData>(`${SPRINTS_PATH}/${sprintId}/burndown`);
  return response.data;
}
